import { useState, useEffect, useRef } from 'react';
import { pluralize } from '@utils/textHelper';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getFirstName } from '@api/selectors/auth';
import useVisibilityChangeListener from './useVisibilityChangeListener';
import { getTodayDate } from '../planner-utils/date-time-helper';

const morningStartHour = 24;
const afternoonStartHour = 12;
const eveningStartHour = 16;

const oneHourInMs = 1000 * 60 * 60;
const morningStartInMs = oneHourInMs * morningStartHour;
const afternoonStartInMs = oneHourInMs * afternoonStartHour;
const eveningStartInMs = oneHourInMs * eveningStartHour;
const { date, dateInMs, hours } = getTodayDate();

const getTimeToNextBoundary = () => {
  const timeNow = dateInMs;
  const timeElapsedTodayInMs = timeNow - date.setHours(0, 0, 0, 0);
  let timeout = 0;
  if (timeElapsedTodayInMs < afternoonStartInMs)
    timeout = afternoonStartInMs - timeElapsedTodayInMs;
  else if (timeElapsedTodayInMs < eveningStartInMs)
    timeout = eveningStartInMs - timeElapsedTodayInMs;
  else timeout = morningStartInMs - timeElapsedTodayInMs;
  return timeout + 10; // buffer
};

const getGreetingData = (hour, userFirstName, eventsCount) => {
  let phrase = '';
  let color = 'var(--color-i-black)';
  let stopTimer = false;
  const isMorning = hour < afternoonStartHour;
  if (isMorning) phrase = 'morning';
  else if (hour < eveningStartHour) phrase = 'afternoon';
  else {
    phrase = 'evening';
    color = 'var(--color-i-gray-9)';
    stopTimer = true;
  }
  return {
    imgSrc: `planner/greeting/${phrase}.svg`,
    imgAlt: phrase,
    title: `Good ${phrase}, ${userFirstName}`,
    info: `You have ${
      eventsCount === 0 ? 'no events' : pluralize(eventsCount, 'event')
    } today`,
    color,
    stopTimer
  };
};

const useGreetingTimer = (eventsCount) => {
  const userFirstName = useMyInfo(getFirstName);
  const [greetingData, setGreetingData] = useState(() =>
    getGreetingData(hours, userFirstName, eventsCount)
  );
  const isTabInFocus = useVisibilityChangeListener();
  const timerRef = useRef(null);
  useEffect(() => {
    if (isTabInFocus) {
      const setGreetingTimer = () => {
        const { stopTimer, ...greetingCardData } = getGreetingData(
          hours,
          userFirstName,
          eventsCount
        );
        setGreetingData(greetingCardData);
        if (!stopTimer) {
          timerRef.current = setTimeout(() => {
            setGreetingTimer();
          }, getTimeToNextBoundary());
        }
      };
      setGreetingTimer();
      return () => {
        clearTimeout(timerRef.current);
      };
    }
  }, [eventsCount, isTabInFocus, userFirstName]);
  return greetingData;
};

export default useGreetingTimer;
